// Telegram WebApp wrapper.
// Safe to call from any scene — every fn no-ops gracefully when the app is
// loaded outside Telegram (browser dev). All listeners are idempotent so scenes
// can wire on mount and unwire on destroy without leaks.

const tg = () => window.Telegram?.WebApp || null;

// -------- Haptics --------
// kinds: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' (impactOccurred)
//        'success' | 'warning' | 'error' (notificationOccurred)
//        'selection' (selectionChanged)
export function haptic(kind = 'light') {
  const h = tg()?.HapticFeedback;
  if (!h) return;
  try {
    if (kind === 'success' || kind === 'warning' || kind === 'error') {
      h.notificationOccurred(kind);
    } else if (kind === 'selection') {
      h.selectionChanged();
    } else {
      h.impactOccurred(kind);
    }
  } catch {}
}

// -------- MainButton --------
// Sticky bottom button rendered natively by Telegram.
// Pattern:
//   const off = mainButton.show('TAP TO START', () => doStart());
//   ... later on destroy: off();
export const mainButton = {
  show(text, onClick, { progress = false, color, textColor } = {}) {
    const mb = tg()?.MainButton;
    if (!mb) return () => {};
    mb.setText(text);
    if (color) mb.color = color;
    if (textColor) mb.textColor = textColor;
    if (progress) mb.showProgress(false);
    else mb.hideProgress();
    const handler = () => onClick?.();
    mb.onClick(handler);
    mb.show();
    return () => {
      try { mb.offClick(handler); } catch {}
      mb.hide();
      mb.hideProgress();
    };
  },
  setText(text) { tg()?.MainButton?.setText(text); },
  setBusy(busy) {
    const mb = tg()?.MainButton;
    if (!mb) return;
    if (busy) { mb.showProgress(false); mb.disable(); }
    else { mb.hideProgress(); mb.enable(); }
  },
  hide() { tg()?.MainButton?.hide(); }
};

// -------- BackButton --------
//   const off = backButton.show(() => ctx.goto('menu'));
//   ... on destroy: off();
export const backButton = {
  show(onClick) {
    const bb = tg()?.BackButton;
    if (!bb) return () => {};
    const handler = () => onClick?.();
    bb.onClick(handler);
    bb.show();
    return () => {
      try { bb.offClick(handler); } catch {}
      bb.hide();
    };
  },
  hide() { tg()?.BackButton?.hide(); }
};

// -------- Theme --------
// Returns 'dark' | 'light' from Telegram. Default 'dark' outside TG.
export function colorScheme() {
  return tg()?.colorScheme || 'dark';
}

// True if inside Telegram WebView and openInvoice etc. are usable.
export function isTelegram() {
  return !!tg()?.initData;
}

// -------- Viewport / Fullscreen --------
// Call once at app boot (after ready()). Tries true fullscreen (Bot API 8.0+),
// falls back to expand() on older clients. Also blocks the swipe-down-to-close
// gesture so users can't accidentally bail mid-tap. Header/bg colors match the
// app dark theme to remove the Telegram chrome seam.
//
// Safe to call multiple times; each underlying method is idempotent.
export function enterFullscreen() {
  const w = tg();
  if (!w) return;
  try { w.ready(); } catch {}
  try { w.expand(); } catch {}
  // Fullscreen (8.0+) — promise-based, throws on devices that don't support it.
  if (typeof w.requestFullscreen === 'function') {
    try { w.requestFullscreen(); } catch {}
  }
  // Block swipe-down close — important once we hide MainButton because the
  // visual "stop" anchor at the bottom of the chrome is gone.
  if (typeof w.disableVerticalSwipes === 'function') {
    try { w.disableVerticalSwipes(); } catch {}
  }
  // Match Telegram chrome to our dark BG so the seam disappears.
  try { w.setHeaderColor && w.setHeaderColor('#06030f'); } catch {}
  try { w.setBackgroundColor && w.setBackgroundColor('#06030f'); } catch {}
  try { w.setBottomBarColor && w.setBottomBarColor('#06030f'); } catch {}
}
